import { useState, useRef, useCallback, useEffect } from 'react';

const BGM_SRC = '/static/bgm.mp3';

const isDev = process.env.NODE_ENV === 'development';

export function useBgm() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mutedRef = useRef(false);
  const [isMuted, setIsMuted] = useState(false);

  // Preload audio on mount so it’s ready on first click (required by some browsers)
  useEffect(() => {
    if (isDev) return;
    const audio = new Audio();
    audio.preload = 'auto';
    audio.loop = true;
    audio.volume = 1;
    audio.src = BGM_SRC;
    audio.addEventListener('error', () => {
      console.warn('[BGM] Failed to load audio. Add public/static/bgm.mp3 to enable BGM.');
    });
    audioRef.current = audio;
    return () => {
      audio.src = '';
      audioRef.current = null;
    };
  }, []);

  const startBgm = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!mutedRef.current) {
      const p = audio.play();
      if (p !== undefined) {
        p.catch((e) => {
          console.warn('[BGM] play() failed:', e?.name, e?.message);
        });
      }
    }
  }, []);

  const toggleMuted = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      mutedRef.current = next;
      const audio = audioRef.current;
      if (audio) {
        if (next) {
          audio.pause();
        } else {
          audio.play().catch((e) => console.warn('[BGM] play() failed:', e?.name));
        }
      }
      return next;
    });
  }, []);

  return { isMuted, toggleMuted, startBgm };
}
